/**
 * Хранилище: что на самом деле лежит в MinIO.
 *
 * Отдельный экран, потому что MinIO становится тем местом, куда стекается
 * всё. Топология показывает потоки и объём, матрица — состояние объектов, а
 * «сколько снимков у этого сайта и насколько глубоко они уходят» не
 * показывает никто.
 *
 * Глубина — не украшение. Один снимок и тридцать это разная защита от ошибки,
 * замеченной через неделю: с одним снимком «вернуть как было позавчера»
 * невозможно, сколько бы места копия ни занимала.
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';
import { Hint } from '../components/Hint.tsx';
import { BrandIcon } from '../components/BrandIcon.tsx';
import { ago, agoPhrase, bytes, count } from '../lib/format.ts';
import type { BucketView, StorageSnapshot } from '../../shared/protocol/storage.ts';

const ROLE_LABEL: Record<BucketView['role'], string> = {
  agents: 'ведро агента',
  pbs: 'чанк-хранилище PBS',
  other: 'назначение не объявлено',
};

/**
 * Неизменяемость — главное свойство бакета с копиями, и у него три разных
 * состояния, которые нельзя показывать одинаково.
 *
 * COMPLIANCE не снимается никем до истечения срока, включая нас самих, — это
 * единственное, что защищает от вымогателя, добравшегося до наших ключей.
 * GOVERNANCE снимается тем, у кого есть право, то есть тем же самым ключом.
 * Разница между ними — вся суть Object Lock.
 */
function Immutability({ b }: { b: BucketView }): React.JSX.Element {
  const state =
    b.objectLock === 'compliance' ? 'ok'
    : b.objectLock === 'governance' ? 'warn'
    : b.versioning === 'enabled' ? 'weak'
    : 'none';

  const word =
    state === 'ok' ? `COMPLIANCE ${b.lockDays ?? '?'} дн`
    : state === 'warn' ? `GOVERNANCE ${b.lockDays ?? '?'} дн`
    : state === 'weak' ? 'только версии'
    : 'ничем не защищено';

  return (
    <Hint content={
      <div className="hint__body">
        <b className="hint__title">Неизменяемость</b>
        {state === 'ok' && (
          <p className="hint__text">
            COMPLIANCE: объект нельзя удалить или переписать до конца срока
            <b> никому</b>, включая нас. Это единственный режим, который держит
            вымогателя, добравшегося до ключей.
          </p>
        )}
        {state === 'warn' && (
          <p className="hint__text hint__text--warn">
            GOVERNANCE снимается тем, у кого есть право, — то есть тем же ключом,
            которым пишут. От случайного удаления защищает, от злого умысла нет.
            Перед первым боевым проходом переключить на COMPLIANCE.
          </p>
        )}
        {state === 'weak' && (
          <p className="hint__text hint__text--warn">
            Версии включены, блокировки нет: прежняя версия остаётся, но её
            можно удалить. Защита от опечатки, не от намерения.
          </p>
        )}
        {state === 'none' && (
          <p className="hint__text hint__text--warn">
            Ни версий, ни блокировки: перезапись уничтожает прежнее содержимое
            безвозвратно.
          </p>
        )}
        <p className="hint__text">
          Версионирование: {b.versioning === 'enabled' ? 'включено'
            : b.versioning === 'suspended' ? 'приостановлено'
            : b.versioning === 'off' ? 'выключено' : 'неизвестно'}.
        </p>
      </div>
    }>
      <span className={`lock lock--${state}`}>{word}</span>
    </Hint>
  );
}

function Bucket({ b }: { b: BucketView }): React.JSX.Element {
  return (
    <article className={`bkt bkt--${b.role}`}>
      <header className="bkt__head">
        <div className="bkt__id">
          <span className="bkt__icon" aria-hidden="true"><BrandIcon brand="minio" size={16} /></span>
          <h3 className="bkt__name mono">{b.name}</h3>
          <span className="bkt__role">
            {ROLE_LABEL[b.role]}
            {b.agentHost && <> · {b.agentHost}</>}
          </span>
        </div>
        <Immutability b={b} />
      </header>

      <dl className="bkt__stats">
        <div><dt>объектов</dt><dd>{count(b.objects)}</dd></div>
        <div>
          <dt>версий</dt>
          <dd>
            {/*
              Версии считает только административный отчёт хранилища, а он
              пересчитывается по своему расписанию. Молчит — говорим «не
              считались», а не «ноль»: ноль версий при трёх объектах это
              утверждение, которого мы не проверяли.
            */}
            {b.versions == null
              ? <span className="muted" title="Считает административный отчёт MinIO; он пересчитывается по своему расписанию">не считались</span>
              : (
                <>
                  {count(b.versions)}
                  {/* Версий больше объектов — значит объекты переписывались.
                      Для хранилища копий это норма, для агентского — вопрос. */}
                  {b.versions > b.objects && b.objects > 0 && (
                    <span className="muted"> · переписывались</span>
                  )}
                </>
              )}
          </dd>
        </div>
        <div><dt>объём</dt><dd>{bytes(b.bytes)}</dd></div>
        <div><dt>заведён</dt><dd>{b.createdAt ? new Date(b.createdAt).toLocaleDateString('ru-RU') : '—'}</dd></div>
      </dl>

      {b.notEnumerated && <p className="bkt__note">{b.notEnumerated}</p>}

      {b.sets.length === 0 ? (
        <p className="bkt__empty">
          {b.objects === 0 && b.countedBy === 'listing'
            // «Пусто» и «не смотрели» — разные вещи, и различать их обязательно.
            // Пусто — это когда МЫ ПЕРЕЧИСЛИЛИ и ничего не нашли. Числа из
            // административного отчёта на это права не дают: у ведра,
            // созданного минуту назад, там нули, хотя копии уже лежат.
            ? 'Бакет пуст — ни одного объекта.'
            : b.objects === 0
              ? 'Отчёт хранилища ещё не пересчитывался — числа появятся в следующем сборе.'
              : 'Наборы не разобраны.'}
        </p>
      ) : (
        <details className="bkt__wrap">
          {/*
            Наборы свёрнуты, как источники у агента. На четырёх вёдрах экран
            без этого — сплошная простыня таблиц, а вопрос, ради которого сюда
            заходят, звучит короче: сколько тут лежит и насколько свежее.
            Итог в свёрнутом виде отвечает на него, не разворачивая ничего.
          */}
          <summary className="bkt__summary">
            <span className="bkt__count">
              {b.sets.length} {b.sets.length === 1 ? 'набор' : 'наборов'}
            </span>
            <span className="bkt__totals">
              {count(b.sets.reduce((n, x) => n + x.objects, 0))} объектов
              <span className="muted"> · {bytes(b.sets.reduce((n, x) => n + x.bytes, 0))}</span>
              {b.sets.some((x) => x.depth != null) && (
                <span className="muted">
                  {' · '}глубина до {Math.max(...b.sets.map((x) => x.depth ?? 0))}
                </span>
              )}
            </span>
            {(() => {
              // Свежесть — по самому НОВОМУ набору: ведро настолько свежее,
              // насколько свежая последняя копия в нём.
              const newest = b.sets
                .map((x) => x.newestAt)
                .filter((v): v is string => Boolean(v))
                .sort()
                .at(-1);
              return newest
                ? <span className="bkt__fresh">{agoPhrase(newest)}</span>
                : <span className="muted">копий нет</span>;
            })()}
          </summary>
        <table className="table bkt__sets">
          <thead>
            <tr>
              <th scope="col">Набор</th>
              <th scope="col">Объектов</th>
              <th scope="col">Объём</th>
              <th scope="col">
                <Hint content={
                  <div className="hint__body">
                    <b className="hint__title">Глубина копий</b>
                    <p className="hint__text">
                      Сколько снимков лежит. Один снимок и тридцать — разная защита:
                      с одним «вернуть как было позавчера» невозможно, сколько бы
                      места копия ни занимала.
                    </p>
                  </div>
                }>Глубина</Hint>
              </th>
              <th scope="col">Свежесть</th>
            </tr>
          </thead>
          <tbody>
            {b.sets.map((s) => (
              <tr key={s.key}>
                <th scope="row">
                  <span className="set__label">{s.label}</span>
                  <span className="set__detail">{s.detail}</span>
                </th>
                <td>{count(s.objects)}</td>
                <td>{bytes(s.bytes)}</td>
                <td className={s.depth === 0 ? 'set__none' : ''}>
                  {/* null и 0 — разные ответы: «не считали» против «нет». */}
                  {s.depth === null ? <span className="muted">—</span> : count(s.depth)}
                </td>
                <td>
                  {s.newestAt
                    ? <span title={new Date(s.newestAt).toLocaleString('ru-RU')}>{ago(s.newestAt)}</span>
                    : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </details>
      )}
    </article>
  );
}

export function Minio(): React.JSX.Element {
  const { data, error, loading, reload } = useQuery<StorageSnapshot>('minio.get');
  const [busy, setBusy] = useState(false);

  /**
   * Пересобрать снимок хранилища.
   *
   * Экран показывает КЭШ сбора, а не хранилище: перечислять бакеты на каждый
   * заход дорого, этим занят работник по расписанию. Без кнопки владелец
   * обновлял страницу, видел прежние числа и считал поломкой ровно то, как это
   * устроено.
   */
  const refresh = async (): Promise<void> => {
    setBusy(true);
    try {
      await request('minio.refresh');
      reload();
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Хранилище MinIO</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Хранилище MinIO</h2>
        <p className="muted">{loading ? 'Опрашиваем хранилище…' : 'Нет данных.'}</p>
      </section>
    );
  }

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <div className="panel__title-row">
          <h2 className="panel__title">Хранилище MinIO</h2>
          <button type="button" className="button button--small"
                  disabled={busy} onClick={() => void refresh()}
                  title="Перечислить бакеты заново прямо сейчас">
            {busy ? 'Опрашиваем…' : 'Опросить заново'}
          </button>
        </div>

        {data.error ? (
          <p className="alert alert--error">
            {data.error}. Числа ниже — не результат опроса.
          </p>
        ) : (
          <p className="bkt__lead">
            <span className="mono">{data.endpoint}</span> · бакетов {data.totals.buckets},
            объектов {count(data.totals.objects)}, объём {bytes(data.totals.bytes)}.
            {data.totals.objects === 0 && (
              <b className="bkt__warn">
                {' '}Ни одного объекта: сюда ещё ничего не писали.
              </b>
            )}
          </p>
        )}
      </header>

      <div className="panel__scroll">
        <div className="bkts">
          {data.buckets.map((b) => <Bucket key={b.name} b={b} />)}
          {data.buckets.length === 0 && !data.error && (
            <p className="muted">Ни одного бакета.</p>
          )}
        </div>
      </div>

      <footer className="panel__foot">
        <span className="legend__item">объём и число объектов — из admin API, без перебора ключей</span>
        <span className="panel__stamp">
          {/* Возраст снимка, а не только время: «опрошено 40 минут назад»
              объясняет расхождение с хранилищем само, без вопросов. */}
          опрошено {agoPhrase(data.generatedAt)}, {new Date(data.generatedAt).toLocaleString('ru-RU')}
        </span>
      </footer>
    </section>
  );
}
